import React from 'react';
import { ParticipantStep } from '../../../../../../../types/v3Types';
import { isUserCompleted } from '../../../../../../../contexts/User/helpers/userTypeHandlers';
import { isProgramProgressReachedEnd } from '../../../../../../../selectors/participant/isProgramProgressReachedEnd';
import { IFollowingEntity } from '../../../utils';
import { Props } from '../../interfaces';
import { useExperiments } from '@wix/yoshi-flow-editor';
import { StepControlsV1 } from './StepControlsV1';
import { FinishedNotification, StepControlsV2 } from './StepControlsV2';
import { Challenges } from '../../../../../../../editor/types/Experiments';
import { sidebarLayoutDataHooks } from '../../sidebarLayoutDataHooks';
import { FormQuizControlsV1 } from './FormQuizControlsV1';
import { FormQuizControlsV2 } from './FormQuizControlsV2';

export interface BaseControlsProps {
  props: Props;
  currentStep: ParticipantStep | null;
  isProgramFinished: boolean;
  getQuizData: () => any;
  onStepResolve: (
    step: ParticipantStep,
    options?: { background?: boolean },
  ) => Promise<void>;
  onNextEntity: (
    nextEntity: IFollowingEntity,
    withBI?: boolean,
  ) => Promise<void>;
}

export const BaseControls: React.FC<BaseControlsProps> = ({
  props,
  currentStep,
  isProgramFinished,
  getQuizData,
  onStepResolve,
  onNextEntity,
}) => {
  const {
    challengeData,
    participant,
    isParticipantInSuspendedState,
    isParticipantInLockedState,
  } = props;
  const isStepActive = !!currentStep?.id;
  const isParticipantInNegativeState =
    isParticipantInSuspendedState || isParticipantInLockedState;
  const { isQuiz } = getQuizData();

  const { experiments } = useExperiments();

  const isUniteNextAndCompleteEnabled = experiments.enabled(
    Challenges.uniteNextAndCompleteButton,
  );

  const isCompletedChallenge =
    isUserCompleted(participant?.transitions?.[0]?.state) ||
    isProgramProgressReachedEnd(challengeData?.challenge, participant) ||
    isProgramFinished;

  const shouldShowBaseControls =
    (isStepActive || isQuiz) && !isParticipantInNegativeState;

  if (isCompletedChallenge) {
    return <FinishedNotification />;
  }

  if (!shouldShowBaseControls) {
    return null;
  }

  if (isQuiz) {
    if (isUniteNextAndCompleteEnabled) {
      return (
        <FormQuizControlsV2
          steps={props.participantSteps}
          sections={props.listParticipantSections}
          onNextEntity={(entity) => onNextEntity(entity)}
        />
      );
    }

    return (
      <FormQuizControlsV1 onNextEntity={(entity) => onNextEntity(entity)} />
    );
  }

  if (isStepActive) {
    if (isUniteNextAndCompleteEnabled) {
      return (
        <StepControlsV2
          completeBtnDataHook={sidebarLayoutDataHooks.stepCompleteBtn()}
          nextBtnDataHook={sidebarLayoutDataHooks.stepNextBtn()}
          currentStep={currentStep}
          steps={props.participantSteps}
          sections={props.listParticipantSections}
          buttonState={props.buttonState}
          onStepResolve={onStepResolve}
          onNextEntity={onNextEntity}
        />
      );
    }

    return (
      <StepControlsV1
        completeBtnDataHook={sidebarLayoutDataHooks.stepCompleteBtn()}
        nextBtnDataHook={sidebarLayoutDataHooks.stepNextBtn()}
        currentStep={currentStep}
        steps={props.participantSteps}
        sections={props.listParticipantSections}
        buttonState={props.buttonState}
        onStepResolve={onStepResolve}
        onNextEntity={onNextEntity}
      />
    );
  }

  return null;
};
